// Cross-tab session plumbing.
//
// Since the OIDC user lives in shared localStorage (see oidcConfig), every
// tab is looking at the same token slot. These helpers are what the tabs use
// to stay in step:
//
//   login in tab A   → oidc.user key appears  → tab B adopts it (no 2nd IdP trip)
//   renew in tab A   → oidc.user key changes  → tab B re-reads it (see below)
//   logout in tab A  → oidc.user key removed  → tab B logs itself out
//
// The renew case matters more than it looks: each tab keeps its OWN in-memory
// copy of the user. If Zitadel rotates refresh tokens, tab A's renew replaces
// the shared refresh token and tab B's in-memory copy becomes a dead one — its
// next renew fails with invalid_grant and the user gets a bogus "session
// expired". Re-reading the stored user keeps every tab on the live token.

import userManager from './userManager';
import {OIDC_USER_STORAGE_KEY} from './oidcConfig';
import {decodeJwtPayload} from './tokenUtils';
import {derivePrimaryRole, effectiveRoles} from './roles';
import {addToLocalStorage, removeFromLocalStorage, setCookie} from '../lib/utils';
import {IMPERSONATION_STORAGE_KEY} from '../lib/impersonation';

export {OIDC_USER_STORAGE_KEY};

// Written (never read back) purely to fire a `storage` event in the other
// tabs. The oidc.user removal already signals logout on its own; this is the
// belt-and-braces path for logouts that don't reach signoutRedirect (the
// legacy fallback in AccountMenu) and it makes the intent explicit rather
// than inferred from an empty key.
export const LOGOUT_BROADCAST_KEY = 'combird_logout_broadcast';

export const ACTOR_TOKEN_STORAGE_KEY = 'combird_actor_token';

// Per-tab (sessionStorage, deliberately) marker meaning "this tab was signed
// out because another tab signed out". It suppresses the automatic
// signinRedirect that AuthGuard/Login would otherwise fire the moment this
// tab lands on the login page.
//
// Without it there is a live race: tab A calls signoutRedirect, tab B reacts
// and bounces to /login, /login auto-redirects to Zitadel — and if tab B's
// /authorize lands BEFORE tab A's end_session finishes, the still-valid SSO
// cookie signs tab B straight back in and the logout silently un-does itself.
const SIGNED_OUT_FLAG_KEY = 'combird_signed_out';

export const markSignedOut = () => {
  try {
    sessionStorage.setItem(SIGNED_OUT_FLAG_KEY, '1');
  } catch { /* storage unavailable — worst case we auto-retry the signin */ }
};

export const wasSignedOutRemotely = () => {
  try {
    return sessionStorage.getItem(SIGNED_OUT_FLAG_KEY) === '1';
  } catch {
    return false;
  }
};

// Called when the user explicitly asks to sign in again — clears the
// suppression so the normal auto-redirect resumes.
export const clearSignedOutFlag = () => {
  try {
    sessionStorage.removeItem(SIGNED_OUT_FLAG_KEY);
  } catch { /* nothing to clear */ }
};

// Keys written by addToLocalStorage on signin — clear them before each new
// login so a partial overwrite never leaves us with mixed-session state
// (e.g. fresh token + previous session's role). See auth_state_lifecycle.md.
const LEGACY_AUTH_LOCAL_STORAGE_KEYS = [
  'token', 'userId', 'tenantId', 'tenantName', 'username', 'role', 'roles'
];

export const clearLegacyAuthState = () => {
  LEGACY_AUTH_LOCAL_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  clearImpersonation();
};

export const clearImpersonation = () => {
  try {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    sessionStorage.removeItem(ACTOR_TOKEN_STORAGE_KEY);
  } catch { /* storage unavailable — nothing to clear */ }
};

// Derive the shared app profile (what the header, sidebar and AuthRoute
// render from) from an access token. The token is the source of truth; the
// profile always follows it. Used both at signin and when a tab notices the
// two have drifted apart.
export const syncProfileFromToken = (accessToken) => {
  if (!accessToken) return;
  const claims = decodeJwtPayload(accessToken) || {};

  // Roles come from the combird_roles custom claim (set by the Zitadel action).
  // effectiveRoles = current channel (sms) roles ∪ platform-tier roles
  // (internal-tools). See src/auth/roles.js for why this is NOT a blind
  // flatten of all projects.
  const roles = effectiveRoles(claims);

  addToLocalStorage({
    token: accessToken,
    userId: claims.sub || '',
    tenantId: claims.org_id || '',
    tenantName: claims.org_name || '',
    username: claims.username || '',
    role: derivePrimaryRole(roles),
    roles
  });
};

// Set by the tab that INITIATES a logout, before it broadcasts.
//
// The other tabs tear themselves down by clearing the shared `oidc.*` keys
// (routeToLogin), and those removals echo back here as storage events — which
// this tab would otherwise read as "somebody else logged me out", flagging
// itself signed-out and firing a second navigation that races its own
// signoutRedirect. That race is what left the initiating tab showing the
// signed-out card after Zitadel returned it to the post-logout URL.
//
// In-memory is deliberate: the flag only has to outlive the redirect, and the
// tab is leaving the document anyway.
let loggingOut = false;

export const markLoggingOut = () => { loggingOut = true; };

export const isLoggingOut = () => loggingOut;

// Tell the other tabs a user-initiated logout happened.
export const broadcastLogout = () => {
  try {
    // Value is irrelevant — only the change fires the event. It must differ
    // from the previous value or browsers suppress the event, hence the
    // timestamp.
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    localStorage.removeItem(LOGOUT_BROADCAST_KEY);
  } catch { /* storage unavailable — oidc.user removal still signals it */ }
};

// Tear down this tab's session state. Does NOT re-broadcast: a tab reacting
// to someone else's logout must not trigger another round, or two tabs bounce
// the signal back and forth forever.
export const clearLocalSession = () => {
  clearImpersonation();
  setCookie('userToken', '', -1);
  setCookie('userId', '', -1);
  removeFromLocalStorage();
  clearLegacyAuthState();
};

// Read the user straight from the shared store, bypassing this tab's
// in-memory copy.
export const loadStoredUser = () => userManager.getUser().catch(() => null);

// A tab parked on the signed-out card can be revived by a login that happened
// in ANOTHER tab — the shared store already holds a live user. Normally
// SessionIdentityGuard adopts it off the `storage` event, but that event is
// missed when it fires while this tab is between documents (mid hard-nav),
// and the tab then sits on the card with a valid session one key away. The
// manual "sign in again" paths call this first so they don't round-trip the
// IdP for a session we already have.
//
// Returns true when a session was adopted (and a reload is under way).
export const adoptExistingSession = async () => {
  const stored = await loadStoredUser();
  if (!stored?.access_token || stored.expired) return false;
  syncProfileFromToken(stored.access_token);
  // Clear before the reload, or the card renders again for the tick before
  // oidc-client-ts reports the restored user as authenticated.
  clearSignedOutFlag();
  // Full reload, same as SessionIdentityGuard's adopt: roles, Redux state and
  // any closure-captured token have to be rebuilt against the new identity.
  window.location.reload();
  return true;
};

export const subjectOf = (accessToken) =>
  decodeJwtPayload(accessToken)?.sub || '';

export const roleFromToken = (accessToken) => {
  const claims = decodeJwtPayload(accessToken);
  if (!claims) return '';
  return derivePrimaryRole(effectiveRoles(claims));
};

// One-reload budget per tab for the profile realign, so a profile that keeps
// getting rewritten behind us can't put the tab in a reload loop.
const REALIGNED_FLAG_KEY = 'combird_profile_realigned';

export const hasRealigned = () => {
  try {
    return sessionStorage.getItem(REALIGNED_FLAG_KEY) === '1';
  } catch {
    return true; // no storage → assume spent, i.e. never reload
  }
};

export const markRealigned = () => {
  try {
    sessionStorage.setItem(REALIGNED_FLAG_KEY, '1');
  } catch { /* storage unavailable — the reload just won't be rate-limited */ }
};
